import type { FC } from 'react';
import { Show } from '@components/Show';
import { Alert, AlertDescription, AlertTitle } from '@components/ui/alert';
import { cn } from 'cn';
import type { LucideIcon } from 'lucide-react';
import { CircleX, Gem, SearchCheck, Timer, TriangleAlert } from 'lucide-react';

import type { GuidanceBlock } from '../utils/readiness';

import { GuidanceHero } from './GuidanceHero';

const VALUE_PROPS: { Icon: LucideIcon; iconClasses: string; title: string; body: string }[] = [
  {
    Icon: SearchCheck,
    iconClasses: 'bg-teal-50 text-teal-600',
    title: 'Spot gaps and risks',
    body: 'Catch common issues early: missing detail, open terms, structure problems, content under the wrong heading.',
  },
  {
    Icon: Gem,
    iconClasses: 'bg-violet-50 text-violet-600',
    title: 'Improve clarity',
    body: 'Make the contract easier to review and understand, with consistent wording and figures throughout.',
  },
  {
    Icon: Timer,
    iconClasses: 'bg-orange-50 text-orange-600',
    title: 'Save time in review',
    body: 'Speed up review and reduce rework by settling compliance points before review begins.',
  },
];

interface IntroPanelProps {
  /** Why a scan cannot run right now; the rest of the panel dims behind it. */
  block?: GuidanceBlock;
}

/** What the drawer shows before any scan has run on this contract. */
export const IntroPanel: FC<IntroPanelProps> = ({ block }) => {
  const dimmed = block ? 'opacity-40' : undefined;
  return (
    <div data-testid="pbqa-intro">
      <div className={dimmed}>
        <GuidanceHero title="Improve your contract before review">
          Get guidance on this contract as it stands, with suggestions for how to make it clearer, more consistent and
          more complete before review.
        </GuidanceHero>
      </div>
      <Show when={Boolean(block)}>
        {block && (
          <div className="px-4 pb-6">
            <Alert
              variant={block.tone}
              className={cn(
                'shadow-sm',
                block.tone === 'danger' ? '[&>svg]:text-error-600' : '[&>svg]:text-warning-600',
              )}
              data-testid="pbqa-guidance-block"
            >
              {block.tone === 'danger' ? <CircleX aria-hidden /> : <TriangleAlert aria-hidden />}
              <AlertTitle>{block.title}</AlertTitle>
              <AlertDescription>
                <p>{block.body}</p>
              </AlertDescription>
            </Alert>
          </div>
        )}
      </Show>
      <ul className={cn('flex flex-col gap-5 px-6 pb-8', dimmed)}>
        {VALUE_PROPS.map(({ Icon, iconClasses, title, body }) => (
          <li key={title} className="flex gap-4">
            <span className={cn('flex size-9 shrink-0 items-center justify-center rounded-lg', iconClasses)}>
              <Icon className="size-4" aria-hidden />
            </span>
            <div>
              <p className="text-sm font-semibold text-gray-900">{title}</p>
              <p className="mt-0.5 text-xs leading-relaxed text-gray-500">{body}</p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};
